import React from 'react';
import api from '../services/api';
import PainelIndicadores from './PainelIndicadores';

type ReportRow = Record<string, any>;

type ReportColumn = {
  key: string;
  label: string;
  format?: (value: any, row: ReportRow) => string;
};

type ReportConfig = {
  key: string;
  titulo: string;
  descricao: string;
  endpoint?: string;
  colunas: ReportColumn[];
  customRows?: () => Promise<ReportRow[]>;
};

const REPORTS: ReportConfig[] = [
  {
    key: 'creches',
    titulo: 'Creches',
    descricao: 'Dados institucionais das creches cadastradas.',
    endpoint: '/creches',
    colunas: [
      { key: 'nome_fantasia', label: 'Nome Fantasia' },
      { key: 'cnpj', label: 'CNPJ' },
      { key: 'email', label: 'E-mail' },
      { key: 'telefone', label: 'Telefone' },
      { key: 'capacidade_instalada', label: 'Capacidade' },
    ],
  },
  {
    key: 'turmas_professores',
    titulo: 'Turmas e Professores',
    descricao: 'Lista das turmas com nomes dos professores vinculados.',
    colunas: [
      { key: 'nome_creche', label: 'Creche' },
      { key: 'nome_turma', label: 'Turma' },
      { key: 'faixa_etaria', label: 'Faixa Etaria' },
      { key: 'turno', label: 'Turno' },
      { key: 'professores', label: 'Professores' },
    ],
    customRows: async () => {
      const [turmasResp, usersResp, crechesResp] = await Promise.all([
        api.get('/turmas'),
        api.get('/users'),
        api.get('/creches'),
      ]);

      const turmas = (turmasResp.data || []) as any[];
      const users = (usersResp.data || []) as any[];
      const creches = (crechesResp.data || []) as any[];

      return turmas.map((turma) => {
        const professores = users
          .filter((u) => Number(u.id_turma || 0) === Number(turma.id))
          .filter((u) => {
            const p = String(u.perfil || u.tipo_usuario || '').toLowerCase();
            return p.includes('professor') || p.includes('educador') || p.includes('funcionario');
          })
          .map((u) => u.nome)
          .filter(Boolean)
          .join(', ');

        const nomeCreche = creches.find((c) => Number(c.id) === Number(turma.creche_id))?.nome || 'Nao informada';

        return {
          nome_creche: nomeCreche,
          nome_turma: turma.nome,
          faixa_etaria: turma.faixa_etaria,
          turno: turma.turno,
          professores: professores || 'Sem professor vinculado',
        };
      });
    },
  },
  {
    key: 'alunos',
    titulo: 'Criancas',
    descricao: 'Relatorio de criancas por turma e responsavel.',
    endpoint: '/alunos',
    colunas: [
      { key: 'nome', label: 'Nome' },
      { key: 'data_nascimento', label: 'Nascimento' },
      { key: 'responsavel', label: 'Responsavel' },
      { key: 'telefone_responsavel', label: 'Telefone' },
      { key: 'turma_id', label: 'Turma ID' },
    ],
  },
  {
    key: 'funcionarios',
    titulo: 'Funcionarios',
    descricao: 'Equipe administrativa e pedagogica.',
    endpoint: '/funcionarios',
    colunas: [
      { key: 'codigo_funcionario', label: 'Codigo' },
      { key: 'nome', label: 'Nome' },
      { key: 'id_cargo', label: 'Cargo ID' },
      { key: 'celular', label: 'Celular' },
      { key: 'data_admissao', label: 'Admissao' },
    ],
  },
  {
    key: 'usuarios',
    titulo: 'Usuarios do Sistema',
    descricao: 'Acessos e perfis de usuarios.',
    endpoint: '/users',
    colunas: [
      { key: 'nome', label: 'Nome' },
      { key: 'perfil', label: 'Perfil' },
      { key: 'email', label: 'E-mail' },
      { key: 'celular', label: 'Celular' },
      { key: 'ativo', label: 'Ativo', format: (v) => (v ? 'Sim' : 'Nao') },
    ],
  },
  {
    key: 'financeiro',
    titulo: 'Financeiro',
    descricao: 'Mensalidades e status de pagamento.',
    endpoint: '/financeiro/mensalidades',
    colunas: [
      { key: 'aluno_id', label: 'Aluno ID' },
      { key: 'valor', label: 'Valor (R$)' },
      { key: 'vencimento', label: 'Vencimento' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'cardapios',
    titulo: 'Cardapios Semanais',
    descricao: 'Planejamento alimentar por dia da semana.',
    endpoint: '/cardapios',
    colunas: [
      { key: 'dia_semana', label: 'Dia' },
      { key: 'cafe_manha', label: 'Cafe da manha' },
      { key: 'almoco', label: 'Almoco' },
      { key: 'jantar', label: 'Jantar' },
    ],
  },
  {
    key: 'calendario',
    titulo: 'Calendario Escolar',
    descricao: 'Eventos, reunioes e atividades.',
    endpoint: '/calendario',
    colunas: [
      { key: 'titulo', label: 'Evento' },
      { key: 'data', label: 'Data' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'descricao', label: 'Descricao' },
    ],
  },
  {
    key: 'comunicacao',
    titulo: 'Comunicacao',
    descricao: 'Comunicados enviados para familias e equipe.',
    endpoint: '/comunicacao',
    colunas: [
      { key: 'titulo', label: 'Titulo' },
      { key: 'destinatario', label: 'Destinatario' },
      { key: 'data_envio', label: 'Data envio' },
      { key: 'conteudo', label: 'Conteudo' },
    ],
  },
  {
    key: 'atendimentos',
    titulo: 'Atendimentos Especiais',
    descricao: 'Acompanhamentos e registros especiais.',
    endpoint: '/atendimentos-especiais',
    colunas: [
      { key: 'aluno_id', label: 'Aluno ID' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'profissional', label: 'Profissional' },
      { key: 'data', label: 'Data' },
      { key: 'descricao', label: 'Descricao' },
    ],
  },
  {
    key: 'rotina',
    titulo: 'Rotina Institucional',
    descricao: 'Atividades do dia a dia por turma.',
    endpoint: '/rotina',
    colunas: [
      { key: 'titulo', label: 'Atividade' },
      { key: 'horario', label: 'Horario' },
      { key: 'turma_id', label: 'Turma ID' },
      { key: 'descricao', label: 'Descricao' },
    ],
  },
  {
    key: 'familias_responsaveis',
    titulo: 'Familias - Responsaveis',
    descricao: 'Cadastro de responsaveis pelas criancas.',
    endpoint: '/familias/responsaveis',
    colunas: [
      { key: 'nome', label: 'Nome' },
      { key: 'parentesco', label: 'Parentesco' },
      { key: 'telefone', label: 'Telefone' },
      { key: 'email', label: 'E-mail' },
    ],
  },
  {
    key: 'familias_contatos_emergencia',
    titulo: 'Familias - Contatos de Emergencia',
    descricao: 'Contatos alternativos de emergencia.',
    endpoint: '/familias/contatos-emergencia',
    colunas: [
      { key: 'nome', label: 'Nome' },
      { key: 'relacao', label: 'Relacao' },
      { key: 'telefone', label: 'Telefone' },
      { key: 'observacoes', label: 'Observacoes' },
    ],
  },
  {
    key: 'familias_autorizacoes',
    titulo: 'Familias - Autorizacoes',
    descricao: 'Autorizacoes para saida, imagem e atividades.',
    endpoint: '/familias/autorizacoes',
    colunas: [
      { key: 'aluno_id', label: 'Aluno ID' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'status', label: 'Status' },
      { key: 'data', label: 'Data' },
    ],
  },
  {
    key: 'familias_agenda',
    titulo: 'Familias - Agenda',
    descricao: 'Registros de agenda e recados.',
    endpoint: '/familias/agenda',
    colunas: [
      { key: 'titulo', label: 'Titulo' },
      { key: 'data', label: 'Data' },
      { key: 'descricao', label: 'Descricao' },
      { key: 'responsavel', label: 'Responsavel' },
    ],
  },
  {
    key: 'pedagogico_projeto',
    titulo: 'Pedagogico - Projetos',
    descricao: 'Projetos pedagogicos em andamento.',
    endpoint: '/pedagogico/projeto',
    colunas: [
      { key: 'titulo', label: 'Titulo' },
      { key: 'objetivo', label: 'Objetivo' },
      { key: 'periodo', label: 'Periodo' },
      { key: 'turma_id', label: 'Turma ID' },
    ],
  },
  {
    key: 'pedagogico_planejamento',
    titulo: 'Pedagogico - Planejamento',
    descricao: 'Planejamento de atividades pedagogicas.',
    endpoint: '/pedagogico/planejamento',
    colunas: [
      { key: 'tema', label: 'Tema' },
      { key: 'data', label: 'Data' },
      { key: 'atividade', label: 'Atividade' },
      { key: 'turma_id', label: 'Turma ID' },
    ],
  },
  {
    key: 'pedagogico_avaliacoes',
    titulo: 'Pedagogico - Avaliacoes',
    descricao: 'Registros de avaliacoes e desenvolvimento.',
    endpoint: '/pedagogico/avaliacoes',
    colunas: [
      { key: 'aluno_id', label: 'Aluno ID' },
      { key: 'criterio', label: 'Criterio' },
      { key: 'resultado', label: 'Resultado' },
      { key: 'data', label: 'Data' },
    ],
  },
  {
    key: 'pedagogico_portfolio',
    titulo: 'Pedagogico - Portfolio',
    descricao: 'Evidencias e portfolio das criancas.',
    endpoint: '/pedagogico/portfolio',
    colunas: [
      { key: 'aluno_id', label: 'Aluno ID' },
      { key: 'titulo', label: 'Titulo' },
      { key: 'descricao', label: 'Descricao' },
      { key: 'data', label: 'Data' },
    ],
  },
  {
    key: 'administrativo_financeiro',
    titulo: 'Administrativo - Financeiro Interno',
    descricao: 'Lancamentos e controle financeiro administrativo.',
    endpoint: '/administrativo/financeiro',
    colunas: [
      { key: 'titulo', label: 'Titulo' },
      { key: 'categoria', label: 'Categoria' },
      { key: 'valor', label: 'Valor' },
      { key: 'data', label: 'Data' },
    ],
  },
  {
    key: 'administrativo_inventario',
    titulo: 'Administrativo - Inventario',
    descricao: 'Itens e controle patrimonial.',
    endpoint: '/administrativo/inventario',
    colunas: [
      { key: 'item', label: 'Item' },
      { key: 'categoria', label: 'Categoria' },
      { key: 'quantidade', label: 'Quantidade' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'administrativo_licencas',
    titulo: 'Administrativo - Licencas e Alvaras',
    descricao: 'Validade de licencas e documentos.',
    endpoint: '/administrativo/licencas',
    colunas: [
      { key: 'documento', label: 'Documento' },
      { key: 'orgao', label: 'Orgao' },
      { key: 'validade', label: 'Validade' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'administrativo_relatorios',
    titulo: 'Administrativo - Relatorios Internos',
    descricao: 'Relatorios gerenciais administrativos.',
    endpoint: '/administrativo/relatorios',
    colunas: [
      { key: 'titulo', label: 'Titulo' },
      { key: 'periodo', label: 'Periodo' },
      { key: 'responsavel', label: 'Responsavel' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'seguranca_acesso',
    titulo: 'Seguranca - Controle de Acesso',
    descricao: 'Registros de entrada e saida.',
    endpoint: '/seguranca/acesso',
    colunas: [
      { key: 'nome', label: 'Nome' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'entrada', label: 'Entrada' },
      { key: 'saida', label: 'Saida' },
    ],
  },
  {
    key: 'seguranca_monitoramento',
    titulo: 'Seguranca - Monitoramento',
    descricao: 'Ocorrencias e monitoramento.',
    endpoint: '/seguranca/monitoramento',
    colunas: [
      { key: 'ocorrencia', label: 'Ocorrencia' },
      { key: 'setor', label: 'Setor' },
      { key: 'data', label: 'Data' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'seguranca_protocolos',
    titulo: 'Seguranca - Protocolos',
    descricao: 'Protocolos de emergencia e seguranca.',
    endpoint: '/seguranca/protocolos',
    colunas: [
      { key: 'protocolo', label: 'Protocolo' },
      { key: 'categoria', label: 'Categoria' },
      { key: 'revisao', label: 'Revisao' },
      { key: 'responsavel', label: 'Responsavel' },
    ],
  },
  {
    key: 'seguranca_contatos',
    titulo: 'Seguranca - Contatos de Emergencia',
    descricao: 'Contatos uteis para situacoes de emergencia.',
    endpoint: '/seguranca/contatos-emergencia',
    colunas: [
      { key: 'nome', label: 'Nome' },
      { key: 'funcao', label: 'Funcao' },
      { key: 'telefone', label: 'Telefone' },
      { key: 'orgao', label: 'Orgao' },
    ],
  },
];

function esc(text: any) {
  return String(text ?? '-')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export default function RelatoriosPage() {
  async function carregarLinhas(cfg: ReportConfig): Promise<ReportRow[]> {
    if (cfg.customRows) return cfg.customRows();
    if (!cfg.endpoint) return [];
    const { data } = await api.get(cfg.endpoint);
    return Array.isArray(data) ? data : [];
  }

  function abrirDocumentoA4(cfg: ReportConfig, rows: ReportRow[]) {
    const hoje = new Date().toLocaleDateString('pt-BR');

    const linhas = rows.map((row) => {
      const tds = cfg.colunas.map((col) => {
        const raw = row[col.key];
        const val = col.format ? col.format(raw, row) : raw;
        return `<td>${esc(val)}</td>`;
      }).join('');
      return `<tr>${tds}</tr>`;
    }).join('');

    const headCols = cfg.colunas.map((c) => `<th>${esc(c.label)}</th>`).join('');

    const html = `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8" />
<title>${esc(cfg.titulo)} - Relatorio</title>
<style>
@page { size: A4; margin: 14mm; }
* { box-sizing: border-box; }
body { margin: 0; background: #f8fafc; color: #1f2937; font-family: Arial, sans-serif; }
.header { border: 1px solid #dbeafe; background: linear-gradient(120deg, #eff6ff, #ecfeff); border-radius: 10px; padding: 12px; margin-bottom: 10px; }
.header h1 { margin: 0 0 4px; font-size: 22px; color: #0f766e; }
.meta { font-size: 12px; color: #334155; }
.info { margin-top: 6px; font-size: 11px; background: #fef9c3; border: 1px solid #fde68a; border-radius: 8px; padding: 6px 8px; }
.table-wrap { border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; overflow: hidden; }
table { width: 100%; border-collapse: collapse; }
th, td { border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; padding: 8px; font-size: 11px; text-align: left; vertical-align: top; }
th:last-child, td:last-child { border-right: none; }
th { background: #f1f5f9; color: #0f172a; }
tr:last-child td { border-bottom: none; }
.empty { padding: 12px; font-size: 12px; color: #64748b; }
.signatures { margin-top: 14px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.sign { border-top: 1px solid #94a3b8; padding-top: 6px; font-size: 11px; color: #475569; }
.footer { margin-top: 10px; font-size: 10px; color: #64748b; }
.no-print { margin-top: 12px; display: flex; gap: 8px; }
.btn { border: none; padding: 8px 12px; border-radius: 6px; cursor: pointer; }
.btn-print { background: #0f766e; color: #fff; }
.btn-close { background: #e5e7eb; color: #111827; }
@media print { .no-print { display: none; } }
</style>
</head>
<body>
  <div class="header">
    <h1>${esc(cfg.titulo)}</h1>
    <div class="meta">Emitido em ${esc(hoje)}</div>
    <div class="meta">Descricao: ${esc(cfg.descricao)}</div>
    <div class="info">Relatorio em formato A4 para visualizacao em tela e impressao.</div>
  </div>
  <div class="table-wrap">
    ${rows.length ? `<table><thead><tr>${headCols}</tr></thead><tbody>${linhas}</tbody></table>` : '<div class="empty">Nenhum registro encontrado.</div>'}
  </div>
  <div class="signatures">
    <div class="sign">Responsavel Pedagogico</div>
    <div class="sign">Direcao / Gestao</div>
  </div>
  <div class="footer">Documento oficial de acompanhamento institucional.</div>
  <div class="no-print">
    <button class="btn btn-print" onclick="window.print()">Imprimir / Salvar em PDF</button>
    <button class="btn btn-close" onclick="window.close()">Fechar</button>
  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) {
      window.alert('Nao foi possivel abrir o relatorio. Verifique bloqueio de pop-up.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  }

  async function gerarRelatorio(cfg: ReportConfig) {
    try {
      const rows = await carregarLinhas(cfg);
      abrirDocumentoA4(cfg, rows);
    } catch {
      window.alert(`Erro ao gerar relatorio: ${cfg.titulo}`);
    }
  }

  return (
    <div className="relatorios-page">
      <div className="pagina-header">
        <h2 className="pagina-titulo">Central de Relatorios</h2>
      </div>
      <p className="pagina-subtitulo">Indicadores em tempo real e documentos A4 para visualizacao em tela e impressao.</p>

      <PainelIndicadores />

      <h3 style={{ margin: '0 0 4px' }}>Listagens para impressao</h3>

      <div className="relatorios-grid">
        {REPORTS.map((cfg) => (
          <article key={cfg.key} className="relatorio-card">
            <h3>{cfg.titulo}</h3>
            <p>{cfg.descricao}</p>
            <button className="btn-primario" onClick={() => gerarRelatorio(cfg)}>
              Gerar Relatorio A4
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}
